<template>
  <div :class="device.isMobile ? 'py-2' : 'px-4 pb-2 pt-4'">
    <!-- Desktop View -->
    <v-data-table
      v-if="!device.isMobile"
      :headers="headers"
      :items="orders"
      class="striped-table ordera-box-shadow"
      :search="search"
      hide-default-footer
      disable-pagination
      mobile-breakpoint="0"
      :loading="loading"
    >
      <template v-slot:no-data>
        <empty-state
          icon="mdi-package-variant-closed"
          :title="$t('no_orders')"
          :subtitle="$t('no_orders_for_selected_date')"
        />
      </template>

      <template v-slot:item.num="{ item }">
        {{ item.num }}
      </template>

      <template v-slot:item.user_name="{ item }">
        <div class="font-weight-medium">{{ toLatin(item.user_name) }}</div>
      </template>

      <template v-slot:item.restaurant="{ item }">
        <div class="d-flex align-center">
          <v-icon small class="mr-1" color="orange">mdi-store</v-icon>
          {{ toLatin(item.restaurant) }}
        </div>
      </template>

      <template v-slot:item.meals="{ item }">
        <div v-for="(meal, index) in item.meals" :key="index" class="mb-1">
          <span class="font-weight-medium">{{ meal.quantity }}x</span>
          {{ toLatin(meal.name) }}
        </div>
      </template>

      <template v-slot:item.delivery_location="{ item }">
        <div class="d-flex align-center">
          <v-icon small class="mr-1" color="orange">mdi-map-marker</v-icon>
          {{ toLatin(item.delivery_location) }}
        </div>
      </template>

      <template v-slot:item.action="{ item }">
        <circle-button
          icon="mdi-eye"
          @click="viewOrderDetails(item)"
          color="primary"
          :tooltip="$t('order_details')"
        />
      </template>
    </v-data-table>

    <!-- Mobile View -->
    <mobile-data-cards
      v-else
      :headers="mobileHeaders"
      :items="orders"
      :loading="loading"
      item-key="id"
      :fields-per-row="2"
      :bold-fields="['user_name']"
      :show-row-number="false"
      class="px-3"
      @click:item="viewOrderDetails"
    >
      <template v-slot:item.user_name="{ item }">
        <div>{{ toLatin(item.user_name) }}</div>
        <div class="text-caption grey--text">{{ item.user_id }}</div>
      </template>

      <template v-slot:item.restaurant="{ item }">
        <v-chip x-small color="primary" outlined>
          {{ toLatin(item.restaurant) }}
        </v-chip>
      </template>

      <template v-slot:item.meals="{ item }">
        <div v-for="(meal, index) in item.meals" :key="index" class="text-caption">
          {{ meal.quantity }}x {{ toLatin(meal.name) }}
        </div>
      </template>

      <template v-slot:item.delivery_location="{ item }">
        <v-icon x-small class="mr-1" color="orange">mdi-map-marker</v-icon>
        {{ toLatin(item.delivery_location) }}
      </template>
    </mobile-data-cards>

    <!-- Order Details Dialog -->
    <standard-dialog
      v-model="detailsDialog"
      :title="$t('order_details')"
      icon="mdi-package-variant"
      max-width="600"
      hide-actions
    >
      <template v-if="selectedOrder">
        <v-row>
          <v-col cols="12" sm="6">
            <div class="text-caption grey--text">{{ $t("makeOrder") }}</div>
            <div class="font-weight-bold">#{{ selectedOrder.id }}</div>
          </v-col>
          <v-col cols="12">
            <v-divider />
          </v-col>
          <v-col cols="12" sm="6">
            <div class="text-caption grey--text">{{ $t("employee") }}</div>
            <div class="font-weight-bold">{{ toLatin(selectedOrder.user_name) }}</div>
            <div class="text-caption">ID: {{ selectedOrder.user_id }}</div>
          </v-col>
          <v-col cols="12" sm="6">
            <div class="text-caption grey--text">{{ $t("restaurant") }}</div>
            <v-chip small color="primary" outlined>
              {{ toLatin(selectedOrder.restaurant) }}
            </v-chip>
          </v-col>
          <v-col cols="12">
            <v-divider />
          </v-col>
          <v-col cols="12">
            <div class="text-caption grey--text mb-2">{{ $t("meals") }}</div>
            <v-list dense>
              <v-list-item v-for="(meal, index) in selectedOrder.meals" :key="index">
                <v-list-item-content>
                  <v-list-item-title>
                    <span class="font-weight-bold">{{ meal.quantity }}x</span>
                    {{ toLatin(meal.name) }}
                  </v-list-item-title>
                  <v-list-item-subtitle v-if="meal.shift">
                    {{ $t("shift.shift") }} {{ formatShiftNumber(meal.shift) }}
                  </v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action>
                  {{ formatCurrency(meal.price, meal.currency) }}
                </v-list-item-action>
              </v-list-item>
            </v-list>
          </v-col>
          <v-col cols="12">
            <v-divider></v-divider>
          </v-col>
          <v-col cols="12">
            <div class="text-caption grey--text">{{ $t("delivery_location") }}</div>
            <div class="d-flex align-center">
              <v-icon color="orange" class="mr-2">mdi-map-marker</v-icon>
              <span class="font-weight-bold">{{ toLatin(selectedOrder.delivery_location) }}</span>
            </div>
            <div class="text-caption mt-1" v-if="selectedOrder.delivery_notes">
              {{ selectedOrder.delivery_notes }}
            </div>
          </v-col>
        </v-row>
      </template>
    </standard-dialog>
  </div>
</template>

<script>
import EmptyState from "@/components/global/EmptyState.vue";
import MobileDataCards from "@/components/global/MobileDataCards.vue";
import StandardDialog from "@/components/dialogs/StandardDialog.vue";
import limitService from "@/services/limit";
import hasSnackbar from "@/mixins/hasSnackbar";
import hasPrinting from "@/mixins/hasPrinting";
import hasReportPrinting from "@/mixins/report/hasReportPrinting";
import hasExcel from "@/mixins/hasExcel";

export default {
  name: "CompanyMultiRestaurantTabPackages",
  mixins: [hasSnackbar, hasPrinting, hasReportPrinting, hasExcel],
  components: {
    EmptyState,
    MobileDataCards,
    StandardDialog,
  },
  data() {
    return {
      loading: false,
      detailsDialog: false,
      selectedOrder: null,
      report: null,
    };
  },
  computed: {
    companyId() {
      return this.$store.state.company?.id;
    },
    orders() {
      const userGroups = Array.isArray(this.report) ? this.report : [];
      const byOrder = {};

      userGroups.forEach((group) => {
        (group.limitTransactions || []).forEach((t) => {
          if (t.orderable_id === null) return;
          if (t.company_id !== this.companyId) return;
          if (!t.order_id) return;
          if (
            this.selectedRestaurants.length > 0 &&
            !this.selectedRestaurants.includes(t.restaurant_id)
          )
            return;
          const txDate = (t.date || "").substring(0, 10);
          if (txDate < this.dates.from || txDate > this.dates.to) return;

          if (!byOrder[t.order_id]) {
            const user = t.user || {};
            byOrder[t.order_id] = {
              id: t.order_id,
              user_id: user.id,
              user_name: [user.name, user.lastname].filter(Boolean).join(" "),
              restaurant: t.restaurant?.name || "",
              order_time: t.order?.created_at || null,
              meals: [],
              delivery_location: "",
              delivery_notes: null,
            };
          }

          if (t.orderable) {
            byOrder[t.order_id].meals.push({
              name: t.orderable.name || "",
              quantity: parseInt(t.quantity) || 1,
              shift: t.shift,
              price: parseFloat(t.orderable.price) || 0,
              currency: this.restaurantCurrencyMap[t.restaurant_id] || "KM",
            });
          }
        });
      });

      return Object.values(byOrder).map((order, index) => ({
        ...order,
        num: index + 1,
      }));
    },
    restaurantCurrencyMap() {
      const map = {};
      for (const r of this.restaurants) {
        map[r.id] = r.settings?.price || "KM";
      }
      return map;
    },
    restaurants() {
      return this.$store.state.company?.restaurants || [];
    },
    selectedRestaurants() {
      return this.$store.state.report.selectedRestaurants || [];
    },
    selectedDate() {
      const { from } = this.$store.state.report.filters.dates;
      return from;
    },
    dates() {
      const { from, to } = this.$store.state.report.filters.dates;
      return { from, to };
    },
    search() {
      return this.$store.state.report.filters.search || "";
    },
    headers() {
      return [
        { text: this.$t("r_b"), value: "num", sortable: false },
        { text: this.$t("fullname"), value: "user_name" },
        { text: this.$t("restaurant"), value: "restaurant" },
        { text: this.$t("meals"), value: "meals", sortable: false },
        { text: this.$t("delivery_location"), value: "delivery_location" },
        {
          text: this.$t("actions"),
          value: "action",
          sortable: false,
          align: "center",
        },
      ];
    },
    mobileHeaders() {
      return [
        { text: this.$t("fullname"), value: "user_name" },
        { text: this.$t("restaurant"), value: "restaurant" },
        { text: this.$t("meals"), value: "meals" },
        { text: this.$t("delivery_location"), value: "delivery_location" },
      ];
    },
  },
  methods: {
    async fetchData() {
      if (!this.companyId) {
        this.report = null;
        return;
      }
      this.loading = true;
      this.report = null;
      try {
        const { data } = await limitService.getCompanyTransactions(this.companyId, {
          date: this.dates.from,
          date2: this.dates.to,
        });
        this.report = data;
      } catch (error) {
        this.showErrorSnackbar(error, this.$t("error_loading_report"));
      } finally {
        this.loading = false;
      }
    },
    formatShiftNumber(shift) {
      if (!shift) return "";
      const match = String(shift).match(/(\d+)/);
      return match ? match[1] : shift;
    },
    formatCurrency(amount, currency) {
      return (parseFloat(amount) || 0).toFixed(2) + " " + (currency || "KM");
    },
    formatOrdersForExport() {
      return this.orders.map((item, index) => ({
        num: index + 1,
        user_name: item.user_name,
        restaurant: item.restaurant,
        meals: item.meals.map((m) => `${m.quantity}x ${m.name}`).join(", "),
        delivery_location: item.delivery_location,
      }));
    },
    viewOrderDetails(order) {
      this.selectedOrder = order;
      this.detailsDialog = true;
    },
  },
  watch: {
    companyId() {
      this.fetchData();
    },
    dates: {
      handler() {
        this.fetchData();
      },
      deep: true,
    },
    isExcelDownloading() {
      if (!this.isExcelDownloading) return;
      this.downloadExcel({
        fileName: [this.$t("report"), this.$t("packages")].join(" ") + ".xlsx",
        sheets: [
          {
            name: this.$t("packages"),
            headers: this.headers.filter((h) => h.value !== "action"),
            items: this.formatOrdersForExport(),
            title: [this.$t("report"), this.$t("packages")].join(" "),
            subtitle: this.subtitle,
          },
        ],
      });
    },
    isPrinting(isPrinting) {
      if (!isPrinting) return;
      const restaurantNames = this.selectedRestaurants
        .map((id) => this.restaurants.find((r) => r.id === id)?.name)
        .filter(Boolean)
        .map(this.toLatin)
        .join(", ");

      this.printDocument({
        tables: [
          {
            headers: this.headers.filter((h) => h.value !== "action"),
            items: this.formatOrdersForExport(),
            showCaption: true,
          },
        ],
        title: [this.$t("report"), this.$t("packages")].join(": "),
        subtitle: this.subtitle,
        afterTableHtml: `<div style="margin-top: 20px; padding-top: 10px; border-top: 1px solid #ddd;">
          <strong>${this.$t("restaurants")}:</strong> ${restaurantNames}
        </div>`,
        hideDatetime: true,
      });
    },
  },
  mounted() {
    this.fetchData();
  },
};
</script>
